import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Float } from '@react-three/drei';
import * as THREE from 'three';
import { getPlanetTextureByType, getCloudTexture, getRingTexture } from './SolarTextures';

export default function SolarPlanet({
  type = 'earth',
  radius = 4,
  position = [0, 0, 0],
  rotationSpeed = 0.05,
  atmosphereColor = '#60a5fa',
  hasClouds = false,
  hasRings = false,
  axialTilt = 0.44,
  emissiveIntensity = 0.1,
}) {
  const planetRef = useRef();
  const cloudsRef = useRef();
  const ringsRef = useRef();

  // Retrieve or generate procedural textures from cache
  const surfaceTexture = useMemo(() => getPlanetTextureByType(type), [type]);
  const cloudTexture = useMemo(() => (hasClouds ? getCloudTexture() : null), [hasClouds]);
  const ringTexture = useMemo(() => (hasRings ? getRingTexture() : null), [hasRings]);

  useFrame((_, delta) => {
    if (planetRef.current) {
      planetRef.current.rotation.y += delta * rotationSpeed;
    }
    if (cloudsRef.current) {
      cloudsRef.current.rotation.y += delta * (rotationSpeed * 1.35);
      cloudsRef.current.rotation.x += delta * 0.01;
    }
    if (ringsRef.current) {
      ringsRef.current.rotation.z += delta * 0.012;
    }
  });

  return (
    <group position={position}>
      <Float speed={0.7} rotationIntensity={0.06} floatIntensity={0.12}>
        <group rotation={[axialTilt, 0, 0.18]}>
          {/* Main Planetary Body */}
          <mesh ref={planetRef}>
            <sphereGeometry args={[radius, 64, 64]} />
            <meshStandardMaterial
              map={surfaceTexture}
              roughness={type === 'cyber' ? 0.2 : type === 'neptune' ? 0.3 : 0.75}
              metalness={type === 'cyber' ? 0.8 : type === 'volcanic' ? 0.2 : 0.1}
              emissive={type === 'volcanic' ? '#ef4444' : type === 'cyber' ? '#a855f7' : '#000000'}
              emissiveIntensity={type === 'volcanic' || type === 'cyber' ? 0.4 : emissiveIntensity}
            />
          </mesh>

          {/* Cloud Layer Shroud */}
          {hasClouds && cloudTexture && (
            <mesh ref={cloudsRef}>
              <sphereGeometry args={[radius * 1.018, 48, 48]} />
              <meshStandardMaterial
                map={cloudTexture}
                transparent
                opacity={0.85}
                depthWrite={false}
                roughness={1}
              />
            </mesh>
          )}

          {/* Concentric Saturn Ring System - Equatorial Plane */}
          {hasRings && ringTexture && (
            <mesh ref={ringsRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
              <planeGeometry args={[radius * 5.2, radius * 5.2]} />
              <meshStandardMaterial
                map={ringTexture}
                transparent
                opacity={0.94}
                side={THREE.DoubleSide}
                depthWrite={false}
                roughness={0.65}
                metalness={0.1}
              />
            </mesh>
          )}

          {/* Atmospheric Rayleigh Limb Glow */}
          <mesh>
            <sphereGeometry args={[radius * 1.05, 32, 32]} />
            <meshBasicMaterial
              color={atmosphereColor}
              transparent
              opacity={type === 'cyber' ? 0.35 : 0.2}
              side={THREE.BackSide}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>
        </group>
      </Float>
    </group>
  );
}
